import React, { useState, useEffect } from 'react';
import Navigation from '@/components/shared/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { Download, Calendar, TrendingUp, Users, CreditCard, Activity, RefreshCw } from 'lucide-react';
import { analyticsApi, reportsApi } from '@/lib/api';
import { toast } from '@/hooks/use-toast';
import LoadingSpinner from '@/components/shared/LoadingSpinner';

const AdminReports = () => {
  const [timeRange, setTimeRange] = useState('30d');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // State for analytics data
  const [stats, setStats] = useState<any[]>([]);
  const [membershipData, setMembershipData] = useState<any[]>([]);
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [memberTypeData, setMemberTypeData] = useState<any[]>([]);

  // Fetch all analytics data
  const fetchAnalyticsData = async (refresh = false) => {
    try {
      if (refresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      // Fetch all data in parallel
      const [
        analyticsResponse,
        membershipGrowthResponse,
        revenueResponse,
        memberTypesResponse
      ] = await Promise.all([
        analyticsApi.getAnalyticsData(timeRange),
        analyticsApi.getMembershipGrowth(6),
        analyticsApi.getRevenueTrends(6),
        analyticsApi.getMemberTypesDistribution()
      ]);

      // Process analytics stats with proper type handling
      const analyticsData = analyticsResponse as any;
      const analyticsStats = [
        {
          title: 'Total Members',
          value: analyticsData?.total_members?.value?.toString() || '0',
          change: `+${analyticsData?.total_members?.growth || 0}% from last month`,
          icon: Users,
          color: 'text-blue-600'
        },
        {
          title: 'Monthly Revenue',
          value: analyticsData?.monthly_revenue?.formatted_value || '0 TZS',
          change: `+${analyticsData?.monthly_revenue?.growth || 0}% from last month`,
          icon: TrendingUp,
          color: 'text-green-600'
        },
        {
          title: 'Payment Rate',
          value: analyticsData?.payment_rate?.formatted_value || '0%',
          change: `+${analyticsData?.payment_rate?.growth || 0}% from last month`,
          icon: CreditCard,
          color: 'text-purple-600'
        },
        {
          title: 'Active Sessions',
          value: analyticsData?.active_sessions?.value?.toString() || '0',
          change: `${analyticsData?.active_sessions?.weekly_count || 0} this week`,
          icon: Activity,
          color: 'text-orange-600'
        },
      ];

      setStats(analyticsStats);
      setMembershipData(Array.isArray(membershipGrowthResponse) ? membershipGrowthResponse : []);
      setRevenueData(Array.isArray(revenueResponse) ? revenueResponse : []);
      setMemberTypeData(Array.isArray(memberTypesResponse) ? memberTypesResponse : []);

    } catch (error: any) {
      console.error('Failed to fetch analytics data:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data. Using fallback data.",
        variant: "destructive"
      });

      // Fallback to mock data if API fails
      setStats([
        {
          title: 'Total Members',
          value: '90',
          change: '+12% from last month',
          icon: Users,
          color: 'text-blue-600'
        },
        {
          title: 'Monthly Revenue',
          value: '3.0M TZS',
          change: '+15% from last month',
          icon: TrendingUp,
          color: 'text-green-600'
        },
        {
          title: 'Payment Rate',
          value: '94%',
          change: '+2% from last month',
          icon: CreditCard,
          color: 'text-purple-600'
        },
        {
          title: 'Active Sessions',
          value: '24',
          change: '8 this week',
          icon: Activity,
          color: 'text-orange-600'
        },
      ]);

      setMembershipData([
        { month: 'Jan', active: 45, pending: 5, suspended: 2 },
        { month: 'Feb', active: 52, pending: 8, suspended: 1 },
        { month: 'Mar', active: 48, pending: 3, suspended: 3 },
        { month: 'Apr', active: 61, pending: 6, suspended: 2 },
        { month: 'May', active: 58, pending: 4, suspended: 1 },
        { month: 'Jun', active: 65, pending: 7, suspended: 2 },
      ]);

      setRevenueData([
        { month: 'Jan', revenue: 2100000, fees: 1800000, other: 300000 },
        { month: 'Feb', revenue: 2400000, fees: 2000000, other: 400000 },
        { month: 'Mar', revenue: 2200000, fees: 1900000, other: 300000 },
        { month: 'Apr', revenue: 2800000, fees: 2300000, other: 500000 },
        { month: 'May', revenue: 2600000, fees: 2200000, other: 400000 },
        { month: 'Jun', revenue: 3000000, fees: 2500000, other: 500000 },
      ]);

      setMemberTypeData([
        { name: 'Active OG', value: 45, color: '#FF6B35' },
        { name: 'Social', value: 20, color: '#F7931E' },
        { name: 'Youth', value: 15, color: '#1E3A8A' },
        { name: 'Student', value: 10, color: '#059669' },
      ]);
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  // Handle export report
  const handleExportReport = async () => {
    try {
      setIsRefreshing(true);
      const reportData = await reportsApi.exportCustomReport('comprehensive', { time_range: timeRange });
      
      // Create blob and download
      const blob = new Blob([reportData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `basketball-club-report-${timeRange}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Report exported successfully!"
      });
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: "Error",
        description: "Failed to export report",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handle quick report generation
  const handleQuickReport = async (reportType: string) => {
    try {
      setIsRefreshing(true);
      let reportData;
      
      switch (reportType) {
        case 'member-details':
          reportData = await reportsApi.getMemberDetailsReport();
          break;
        case 'payment-history':
          reportData = await reportsApi.getPaymentHistoryReport();
          break;
        case 'financial-summary':
          reportData = await reportsApi.getFinancialSummary(timeRange);
          break;
        case 'activity':
          reportData = await reportsApi.getActivityReport(timeRange);
          break;
        case 'monthly':
          reportData = await reportsApi.getMonthlySummary();
          break;
        default:
          throw new Error('Unknown report type');
      }

      toast({
        title: "Success",
        description: `${reportType.replace('-', ' ')} report generated successfully!`
      });
      
      // You could open a modal or navigate to show the report data
      console.log('Report data:', reportData);
      
    } catch (error) {
      console.error('Report generation failed:', error);
      toast({
        title: "Error",
        description: "Failed to generate report",
        variant: "destructive"
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  // Fetch data on component mount and when time range changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [timeRange]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <p className="mt-4 text-gray-600">Loading analytics data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="text-gray-600 mt-2">
              Track club performance and generate detailed reports
            </p>
          </div>
          <div className="flex space-x-3">
            <Button 
              variant="outline" 
              onClick={() => fetchAnalyticsData(true)}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Last 7 days</SelectItem>
                <SelectItem value="30d">Last 30 days</SelectItem>
                <SelectItem value="90d">Last 3 months</SelectItem>
                <SelectItem value="1y">Last year</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={handleExportReport} disabled={isRefreshing}>
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {stats.map((stat, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                    <p className={`text-sm ${stat.color}`}>{stat.change}</p>
                  </div>
                  <stat.icon className={`h-8 w-8 ${stat.color}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Membership Growth Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Membership Growth</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={membershipData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="active" fill="#059669" name="Active" />
                  <Bar dataKey="pending" fill="#F59E0B" name="Pending" />
                  <Bar dataKey="suspended" fill="#DC2626" name="Suspended" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Member Types Distribution */}
          <Card>
            <CardHeader>
              <CardTitle>Member Types Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={memberTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }: { name: string; percent: number }) => 
                      `${name} ${Math.round(percent * 100)}%`
                    }
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {memberTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Chart */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Revenue Trends</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={revenueData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value: number) => `${(value / 1000000).toFixed(1)}M TZS`} />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#1E3A8A" 
                  strokeWidth={3}
                  name="Total Revenue"
                />
                <Line 
                  type="monotone" 
                  dataKey="fees" 
                  stroke="#059669" 
                  strokeWidth={2}
                  name="Membership Fees"
                />
                <Line 
                  type="monotone" 
                  dataKey="other" 
                  stroke="#F59E0B" 
                  strokeWidth={2}
                  name="Other Income"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Quick Reports */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Reports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => handleQuickReport('member-details')}
                disabled={isRefreshing}
              >
                <Users className="h-6 w-6 mb-2" />
                Member Details Report
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => handleQuickReport('payment-history')}
                disabled={isRefreshing}
              >
                <CreditCard className="h-6 w-6 mb-2" />
                Payment History Report
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => handleQuickReport('financial-summary')}
                disabled={isRefreshing}
              >
                <TrendingUp className="h-6 w-6 mb-2" />
                Financial Summary
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => handleQuickReport('activity')}
                disabled={isRefreshing}
              >
                <Activity className="h-6 w-6 mb-2" />
                Activity Report
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={() => handleQuickReport('monthly')}
                disabled={isRefreshing}
              >
                <Calendar className="h-6 w-6 mb-2" />
                Monthly Summary
              </Button>
              <Button 
                variant="outline" 
                className="h-20 flex-col"
                onClick={handleExportReport}
                disabled={isRefreshing}
              >
                <Download className="h-6 w-6 mb-2" />
                Custom Export
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminReports;
